import React, { useState, useEffect } from 'react';
import { listCoupons, createCoupon, validateCoupon, deactivateCoupon } from '../api/pocketbase';
import { FaPlus, FaEllipsisV, FaCheck, FaTimes, FaTag, FaPercentage, FaDollarSign } from 'react-icons/fa';
import toast from 'react-hot-toast';

const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [validateResult, setValidateResult] = useState(null);
  const [validateCode, setValidateCode] = useState('');
  const limit = 10;

  // Create coupon form state
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    currency: 'aud',
    maxRedemptions: '',
    duration: 'once',
    durationInMonths: '',
    validFrom: '',
    validUntil: '',
    minSubscriptionAmount: '',
    productIds: [],
    description: ''
  });

  useEffect(() => {
    loadCoupons();
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenu && !e.target.closest('.coupon-menu')) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeMenu]);

  const loadCoupons = async () => {
    setIsLoading(true);
    try {
      const response = await listCoupons(limit, currentPage);
      if (response.success) {
        setCoupons(response.coupons || []);
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: formData.code,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        currency: formData.currency,
        maxRedemptions: formData.maxRedemptions || '1',
        duration: formData.duration,
        durationInMonths: formData.durationInMonths || null,
        validFrom: formData.validFrom ? new Date(formData.validFrom).getTime().toString() : null,
        validUntil: formData.validUntil ? new Date(formData.validUntil).getTime().toString() : null,
        minSubscriptionAmount: formData.minSubscriptionAmount || null,
        productIds: formData.productIds.length > 0 ? formData.productIds : [],
        description: formData.description
      };

      const response = await createCoupon(payload);
      if (response.success) {
        toast.success('Coupon created successfully!');
        setShowCreateModal(false);
        setFormData({
          code: '',
          discountType: 'percentage',
          discountValue: '',
          currency: 'aud',
          maxRedemptions: '',
          duration: 'once',
          durationInMonths: '',
          validFrom: '',
          validUntil: '',
          minSubscriptionAmount: '',
          productIds: [],
          description: ''
        });
        loadCoupons();
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast.error('Failed to create coupon');
    }
  };

  const handleValidate = async () => {
    if (!validateCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    try {
      const response = await validateCoupon(validateCode);
      setValidateResult(response);
    } catch (error) {
      console.error('Error validating coupon:', error);
      toast.error('Failed to validate coupon');
    }
  };

  const handleDeactivate = async (couponId) => {
    try {
      const response = await deactivateCoupon(couponId);
      if (response.success) {
        toast.success('Coupon deactivated successfully!');
        setActiveMenu(null);
        loadCoupons();
      }
    } catch (error) {
      console.error('Error deactivating coupon:', error);
      toast.error('Failed to deactivate coupon');
    }
  };

  const openValidateModal = (coupon) => {
    setSelectedCoupon(coupon);
    setValidateCode(coupon.code);
    setValidateResult(null);
    setShowValidateModal(true);
    setActiveMenu(null);
  };

  const formatDiscount = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%`;
    }
    return `${coupon.currency || '$'}${coupon.discountValue}`;
  };

  const formatDuration = (duration, months) => {
    if (duration === 'once') return 'One time';
    if (duration === 'repeating') return months ? `${months} months` : 'Repeating';
    if (duration === 'forever') return 'Forever';
    return duration;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Coupons</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center bg-[#90c740] text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
        >
          <FaPlus className="mr-2" />
          Create Coupon
        </button>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Redemptions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Times Used</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-8 ml-auto"></div></td>
                </tr>
              ))
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  No coupons found. Create your first coupon!
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-gray-900">
                        {coupon?.code}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-gray-900">
                      {coupon.discountType === 'percentage' ? (
                        <FaPercentage className="mr-1 text-gray-400" />
                      ) : (
                        <FaDollarSign className="mr-1 text-gray-400" />
                      )}
                      {formatDiscount(coupon)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {coupon.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {formatDuration(coupon.duration, coupon.durationInMonths)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {coupon.maxRedemptions || 'Unlimited'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {coupon.timesUsed || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="relative coupon-menu">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === coupon.id ? null : coupon.id);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-full"
                      >
                        <FaEllipsisV className="text-gray-500" />
                      </button>
                      {activeMenu === coupon.id && (
                        <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                          <button
                            onClick={() => openValidateModal(coupon)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Validate Coupon
                          </button>
                          <button
                            onClick={() => handleDeactivate(coupon.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            Deactivate Coupon
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && coupons.length > 0 && (
        <div className="flex justify-center items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-200 text-gray-600 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-600">Page {currentPage}</span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={coupons.length < limit}
            className="px-3 py-1 rounded bg-gray-200 text-gray-600 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Coupon Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create New Coupon</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreateCoupon}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., SAVE50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type *</label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value *</label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., 50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="aud">AUD</option>
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                    <option value="gbp">GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="once">One Time</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Months)</label>
                  <input
                    type="number"
                    name="durationInMonths"
                    value={formData.durationInMonths}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="For repeating duration"
                    disabled={formData.duration !== 'repeating'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Redemptions</label>
                  <input
                    type="number"
                    name="maxRedemptions"
                    value={formData.maxRedemptions}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Subscription Amount</label>
                  <input
                    type="number"
                    name="minSubscriptionAmount"
                    value={formData.minSubscriptionAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Minimum amount to apply"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                  <input
                    type="datetime-local"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <input
                    type="datetime-local"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#90c740] text-white rounded-lg hover:bg-green-600"
                >
                  Create Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Validate Coupon Modal */}
      {showValidateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Validate Coupon</h2>
              <button onClick={() => setShowValidateModal(false)} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
              <input
                type="text"
                value={validateCode}
                onChange={(e) => setValidateCode(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter coupon code"
              />
            </div>
            <button
              onClick={handleValidate}
              className="w-full px-4 py-2 bg-[#90c740] text-white rounded-lg hover:bg-green-600 mb-4"
            >
              Validate
            </button>
            {validateResult && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                {validateResult.valid ? (
                  <>
                    <div className="flex items-center text-green-600 mb-2">
                      <FaCheck className="mr-2" />
                      <span className="font-medium">Valid Coupon</span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Code:</strong> {validateResult.coupon?.code}</p>
                      <p><strong>Discount:</strong> {validateResult.coupon?.discountType === 'percentage' ? `${validateResult.coupon?.discountValue}%` : `$${validateResult.coupon?.discountValue}`}</p>
                      <p><strong>Duration:</strong> {validateResult.coupon?.duration}</p>
                      {validateResult.calculation && (
                        <>
                          <p><strong>Original Amount:</strong> ${validateResult.calculation.originalAmount}</p>
                          <p><strong>Discount Amount:</strong> ${validateResult.calculation.discountAmount}</p>
                          <p><strong>Final Amount:</strong> ${validateResult.calculation.finalAmount}</p>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center text-red-600">
                    <FaTimes className="mr-2" />
                    <span className="font-medium">Invalid Coupon</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;